import { ICON_SIZE } from '@components/v2/constants';
import Icon from '@components/v2/ui/Icon/Icon';
import Loading from '@components/v2/ui/Loading/Loading';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { Plus } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import LangEnum from '../../../../../lang/LangEnum';
import FileCard from '../../../../media/components/FileCard/FileCard';
import { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_BYTES } from '../../../../media/useMediaListManager';
import { useTranslation } from '../../../../translation/TranslationContext';
import { CatalogCreationMediaPreview } from '../../catalogCreationForm.types';
import {
  CATALOG_CREATION_MEDIA_ACCEPTED_TYPES,
  CATALOG_CREATION_MEDIA_TILE_SIZE,
} from '../../catalogCreationMedia.consts';
import {
  resolveCatalogCreationMediaRejectionMessage,
  resolveCatalogMediaFileName,
} from '../../catalogCreationMedia.utils';
import { useCatalogCreationMediaDropzoneStyles } from './catalogCreationMediaDropzone.styles';

const CATALOG_MEDIA_HEADER_OPACITY = 0.7;

interface CatalogCreationMediaDropzoneProps {
  label: LangEnum;
  isUploading: boolean;
  preview?: CatalogCreationMediaPreview | null;
  isMultiple?: boolean;
  isEditable?: boolean;
  tileSize?: number | string;
  previewAspectRatio?: string;
  onSelectFiles: (files: File[]) => void;
  onRemove: () => void;
  onPreview?: () => void;
}

const CatalogCreationMediaDropzone = ({
  label,
  isUploading,
  preview,
  isMultiple = false,
  isEditable = false,
  tileSize = CATALOG_CREATION_MEDIA_TILE_SIZE,
  previewAspectRatio,
  onSelectFiles,
  onRemove,
  onPreview,
}: CatalogCreationMediaDropzoneProps) => {
  const { translate } = useTranslation();
  const { classes, cx } = useCatalogCreationMediaDropzoneStyles();

  const { getRootProps, getInputProps, isDragActive, fileRejections, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: isMultiple,
    accept: CATALOG_CREATION_MEDIA_ACCEPTED_TYPES,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0 && (!preview || isEditable)) {
        onSelectFiles(acceptedFiles);
      }
    },
  });

  const translatedLabel = translate(label);
  const rejectionMessage = resolveCatalogCreationMediaRejectionMessage(fileRejections);

  return (
    <Stack className={classes.tile} sx={{ width: tileSize }}>
      <Box
        {...getRootProps({
          className: cx(classes.dropzone, {
            [classes.filledDropzone]: Boolean(preview),
            [classes.dragActive]: isDragActive,
          }),
        })}
      >
        <input {...getInputProps()} />

        {preview && (
          <FileCard
            isSquare
            backgroundShade={200}
            headerOpacity={CATALOG_MEDIA_HEADER_OPACITY}
            url={preview.url}
            mediaType="IMAGE"
            isFileNameHidden
            isMediaIconHidden
            fileName={preview.fileName ?? resolveCatalogMediaFileName(preview.url)}
            mediaAspectRatio={previewAspectRatio}
            onClick={onPreview}
            onRemove={onRemove}
            onEdit={isEditable && !isUploading ? open : undefined}
          />
        )}

        {!preview && !isUploading && (
          <Box
            component="button"
            type="button"
            className={classes.emptyContent}
            aria-label={translate(LangEnum.GLOBAL_CLICK_TO_SELECT_IMAGE_FOR, {
              label: translatedLabel,
            })}
            onClick={open}
          >
            <Icon icon={Plus} size="md" />
          </Box>
        )}

        {isUploading && (
          <Box className={classes.uploadingOverlay}>
            <Loading size={ICON_SIZE.lg} />
          </Box>
        )}
      </Box>

      {rejectionMessage && (
        <Typography variant="micro2" sx={{ color: 'error.main' }}>
          {translate(rejectionMessage, {
            maxSize: String(MAX_IMAGE_SIZE),
          })}
        </Typography>
      )}
    </Stack>
  );
};

export default CatalogCreationMediaDropzone;
